import {DefaultRootState, useSelector} from 'react-redux';

import type {UseQuery} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import type {QueryDefinition} from '@reduxjs/toolkit/query/react';
import type {ListResponseType} from 'sweet-types';

export function createGenericHookReadingFromById<
  UseQueryHookType extends UseQuery<
    QueryDefinition<any, any, any, any, 'sweetAPI'>
  >
>(
  useAPIListHook: UseQueryHookType,
  byIdSelector: (state: DefaultRootState) => {[id: string]: any},
  idGetter: (item: any) => string = (item) => item.id,
  readDataFromByIdDict: (
    data: any,
    byId: {[id: string]: any},
    idGetter: (item: any) => string
  ) => any
): UseQueryHookType {
  // @ts-expect-error bla
  return (...args) => {
    const {data, currentData, ...response} = useAPIListHook(...args);
    const byId = useSelector(byIdSelector);

    return {
      ...response,
      data: readDataFromByIdDict(data, byId, idGetter),
      currentData: readDataFromByIdDict(currentData, byId, idGetter),
    };
  };
}

export function createListHookReadingFromById<
  UseQueryHookType extends UseQuery<
    QueryDefinition<any, any, any, any, 'sweetAPI'>
  >
>(
  useAPIListHook: UseQueryHookType,
  byIdSelector: (state: DefaultRootState) => {[id: string]: any},
  idGetter?: (item: any) => string
): UseQueryHookType {
  return createGenericHookReadingFromById(
    useAPIListHook,
    byIdSelector,
    idGetter,
    <I extends {id: string | number}>(
      data: null | ListResponseType<I>,
      byId: {[id: string]: I},
      idGetter: (item: I) => string
    ): null | ListResponseType<I> =>
      data == null
        ? null
        : {
            ...data,
            results: data.results.map((item) => byId[idGetter(item)]),
          }
  );
}

export function createDetailHookReadingFromById<
  UseQueryHookType extends UseQuery<
    QueryDefinition<any, any, any, any, 'sweetAPI'>
  >
>(
  useAPIListHook: UseQueryHookType,
  byIdSelector: (state: DefaultRootState) => {[id: string]: any},
  idGetter?: (item: any) => string
): UseQueryHookType {
  return createGenericHookReadingFromById(
    useAPIListHook,
    byIdSelector,
    idGetter,
    (data, byId, idGetter) => (data == null ? null : byId[idGetter(data)])
  );
}
